/**
 * i18n Configuration
 * Central configuration for internationalization.
 *
 * Uses full locale codes (e.g., 'en-US', 'en-CA') instead of language-only codes.
 * Locale registry and country data live in ../intl/; this file focuses on
 * translation loading configuration.
 */

import { SUPPORTED_LOCALES, DEFAULT_LOCALE } from '../intl/intl.config';
import type { SupportedLocale } from '../intl/intl.config';

export const defaultLocale: SupportedLocale = DEFAULT_LOCALE;
export const locales = SUPPORTED_LOCALES;

export type Locale = SupportedLocale;

export const namespaces = ['common', 'pdp', 'sidecart'] as const;
export type Namespace = (typeof namespaces)[number];

export const defaultNamespace: Namespace = 'common';

export interface I18nConfig {
  defaultLocale: string;
  locales: readonly string[];
  namespaces: readonly string[];
  defaultNamespace: string;
}

export const i18nConfig: I18nConfig = {
  defaultLocale,
  locales,
  namespaces,
  defaultNamespace,
};

/**
 * Fallback language map for i18next.
 * Allows locale-specific translation files to fall back to the default locale's
 * translations when a key is missing. For example, en-CA and en-GB fall back to en-US.
 */
export const fallbackLngMap: Record<string, string[]> = {
  'es-US': ['en-US'],
  'en-CA': ['en-US'],
  'fr-CA': ['en-CA', 'en-US'],
  'en-GB': ['en-US'],
  'en-AU': ['en-US'],
  'en-NZ': ['en-US'],
  'en-SG': ['en-US'],
  'en-NL': ['en-GB', 'en-US'],
  'en-SE': ['en-GB', 'en-US'],
  'en-DE': ['en-GB', 'en-US'],
  'de-DE': ['en-DE', 'en-GB', 'en-US'],
  'en-DK': ['en-GB', 'en-US'],
  'en-FI': ['en-GB', 'en-US'],
  default: ['en-US'],
};
